import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowRight, DollarSign, History, Info, Link2, Loader2, Pencil } from 'lucide-react';
import api from '../../utils/api';
import usePayPermissions from '../../hooks/usePayPermissions';
import { Dialog, Field, FormRow, StatusPill, TableSkeleton } from '../../components/ui';
import {
  RATES, RateLabel, RateInputs, ratesForm, parseRates, rateText, dmy, todayIso, errText, usePayEngine,
} from '../Settings/payRatesKit';

// Worker profile > Pay Rates (owner spec v1.2, section 5). The worker's rates beside the company
// defaults, one start date for an edit, and every version read-only. On a company still paid from
// SCHADS pay groups this card is a short note and a link to the switch: nothing here changes pay.

const SOURCE = { default: 'From the defaults', profile: 'Edited on the profile', seed: 'Starting rates' };

// Rate sync status with Xero (spec 5.2): In sync, Pending (a future start date) or Failed (with the
// Xero error). routes/xeroPayrollRates.js GET /xero-sync/rates/staff/:userId.
const SYNC = {
  in_sync: { tone: 'success', label: 'In sync' },
  pending: { tone: 'warning', label: 'Pending' },
  failed: { tone: 'danger', label: 'Failed' },
  not_synced: { tone: 'neutral', label: 'Not synced' },
  not_mapped: { tone: 'neutral', label: 'Not mapped' },
  not_linked: { tone: 'neutral', label: 'Not linked' },
};
function SyncPill({ s }) {
  if (!s) return null;
  const m = SYNC[s.status] || SYNC.not_synced;
  const label = s.status === 'pending' && s.from ? `Pending from ${dmy(s.from)}` : m.label;
  return <span title={s.error || undefined}><StatusPill tone={m.tone} label={label} /></span>;
}

// Worker Profile > Pay Rates > Xero Employee: the dropdown of Xero Payroll employees (read from Xero
// when opened) and the rate sync status. Linking syncs the employee's pay template straight away.
function XeroEmployeeRow({ userId, xero, canLink, onChanged }) {
  const [options, setOptions] = useState(null);
  const [choice, setChoice] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const linkedName = options && xero.xeroEmployeeId ? ((options.find((o) => o.id === xero.xeroEmployeeId) || {}).name || null) : null;
  const openPicker = async () => {
    setOpen(true);
    setChoice(xero.xeroEmployeeId || '');
    if (options) return;
    try { const { data } = await api.get('/xero-sync/rates/employees'); setOptions(data.employees || []); }
    catch (err) { toast.error(errText(err, 'Could not read your Xero employees')); setOpen(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/xero-sync/rates/staff/${userId}/link`, { xero_employee_id: choice || null });
      const sync = data.sync || {};
      if (!data.linked) toast.success('Unlinked from Xero');
      else if (sync.failed) toast.error(`Linked, but the rate sync failed: ${(sync.results && sync.results[0] && sync.results[0].error) || ''}`, { duration: 9000 });
      else if (sync.synced) toast.success(`Linked to ${data.xeroName}. Rates synced to Xero.`);
      else toast.success(`Linked to ${data.xeroName}${sync.skipped === 'dry_run_required' ? '. Rates sync once the rate sync dry run has been looked at.' : ''}`);
      setOpen(false);
      onChanged();
    } catch (err) { toast.error(errText(err, 'Could not link the Xero employee')); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-slate-100 px-5 py-3 text-[13px]">
      <span className="font-medium text-slate-700">Xero Employee</span>
      {!open && (
        <>
          <span className="text-slate-600">{xero.xeroEmployeeId ? (linkedName || 'Linked') : 'Not linked'}</span>
          {xero.xeroEmployeeId && <SyncPill s={{ status: xero.status, from: xero.pendingFrom, error: xero.error }} />}
          {xero.status === 'failed' && xero.error && <span className="text-rose-700">{xero.error}</span>}
          {xero.lastSyncAt && <span className="text-[12px] text-slate-400">Last sync {dmy(xero.lastSyncAt)}</span>}
          {canLink && (
            <button type="button" className="btn-ghost btn-sm" onClick={openPicker}><Link2 size={13} aria-hidden="true" />{xero.xeroEmployeeId ? 'Change' : 'Choose Xero employee'}</button>
          )}
        </>
      )}
      {open && (
        <>
          {!options ? <Loader2 size={14} className="animate-spin text-slate-400" aria-label="Loading Xero employees" /> : (
            <select className="input-field max-w-xs" aria-label="Xero employee" value={choice} onChange={(e) => setChoice(e.target.value)}>
              <option value="">Not linked</option>
              {options.map((o) => (
                <option key={o.id} value={o.id} disabled={!!(o.linkedTo && o.linkedTo.userId !== userId)}>
                  {o.name}{o.calendarName ? ` (${o.calendarName})` : ''}{o.linkedTo && o.linkedTo.userId !== userId ? `, linked to ${o.linkedTo.name}` : ''}
                </option>
              ))}
            </select>
          )}
          <button type="button" className="btn-primary btn-sm" onClick={save} disabled={busy || !options}>{busy && <Loader2 size={13} className="animate-spin" aria-hidden="true" />}Save</button>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setOpen(false)} disabled={busy}>Cancel</button>
        </>
      )}
    </div>
  );
}

function EditDialog({ open, userId, name, current, defaults, onClose, onSaved }) {
  const [form, setForm] = useState(() => ratesForm(current));
  const [startDate, setStartDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(ratesForm(current || defaults));
    setStartDate(todayIso());
    setError('');
  }, [open, current, defaults]);

  const close = () => { if (!busy) onClose(); };

  const save = async (e) => {
    e.preventDefault();
    if (!startDate) { setError('Pick the date the new rates start.'); return; }
    const { rates, error: bad } = parseRates(form);
    if (bad) { setError(bad); return; }
    setBusy(true);
    try {
      await api.post(`/pay-rates/staff/${userId}`, { start_date: startDate, rates });
      toast.success(`Rates saved for ${name || 'this worker'} from ${dmy(startDate)}`);
      setBusy(false);
      onSaved();
      onClose();
    } catch (err) {
      setError(errText(err, "The rates weren't saved. Try again."));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={close} size="lg" dismissable={!busy}
      title="Edit rates"
      description="Change any or all rates. They are saved as a new version from the start date; earlier versions stay in the history."
      footer={(
        <>
          <button type="button" className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          <button type="submit" form="staff-pay-rates-form" className="btn-primary" disabled={busy}>
            {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : null}Save rates
          </button>
        </>
      )}>
      <form id="staff-pay-rates-form" onSubmit={save} className="space-y-4">
        <FormRow>
          <Field label="Start date" required hint="Any date, including a past date. Pay lines from that date that have not been sent are recalculated.">
            {(id) => <input id={id} type="date" className="input-field" value={startDate} onChange={(e) => { setStartDate(e.target.value); setError(''); }} />}
          </Field>
        </FormRow>
        <RateInputs value={form} onChange={(v) => { setForm(v); setError(''); }} compare={current || undefined} compareLabel="Now" idPrefix="staff-rate" disabled={busy} />
        {error && <p className="text-[13px] text-rose-600" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

export default function StaffPayRates({ userId, name }) {
  const orgEngine = usePayEngine();
  // Pay Rates: View to see the card's rates, Pay Rates: Edit to change them (spec section 10).
  const { can } = usePayPermissions();
  const canView = can('pay_rates.view');
  const canEdit = can('pay_rates.edit');
  // Linking a Xero employee is Integrations: Xero (spec section 10).
  const canLink = can('integrations.xero');
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [xero, setXero] = useState(null);

  const loadXero = useCallback(() => {
    api.get(`/xero-sync/rates/staff/${userId}`).then((r) => setXero(r.data || null)).catch(() => setXero(null));
  }, [userId]);
  const load = useCallback(() => {
    setFailed(false);
    api.get(`/pay-rates/staff/${userId}`)
      .then((r) => setData(r.data || {}))
      .catch(() => { setData(null); setFailed(true); });
    loadXero();
  }, [userId, loadXero]);

  // Award-engine companies never call the API: the card is a note only.
  const engine = (data && data.engine) || orgEngine;
  useEffect(() => { if (orgEngine === 'rates' && canView) load(); }, [orgEngine, canView, load]);
  // Employees only: contractors are paid by Xero bills, linked to a Xero contact instead.
  const xeroShown = !!(xero && xero.engine === 'rates' && !xero.contractor && data && data.employment_type !== 'contractor');
  const xeroCol = xeroShown && xero.xeroEmployeeId;

  const history = useMemo(() => {
    const h = data && Array.isArray(data.history) ? [...data.history] : [];
    return h.sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)) || String(b.created_at || '').localeCompare(String(a.created_at || '')));
  }, [data]);

  const header = (actions) => (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-5 py-3">
      <div className="flex items-center gap-2.5">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600"><DollarSign size={15} aria-hidden="true" /></span>
        <h2 className="text-sm font-semibold text-slate-800">Pay Rates</h2>
      </div>
      {actions}
    </div>
  );

  if (engine !== 'rates') {
    return (
      <section id="pay-rates" aria-label="Pay Rates" className="scroll-mt-24 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {header(null)}
        <div className="flex items-start gap-2.5 px-5 py-3.5 text-[13px] text-slate-600">
          <Info size={15} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
          <p>
            This company pays from SCHADS pay groups. To pay workers from their own rate card instead, turn on Pay Rates in{' '}
            <Link to="/settings/pay-rates" className="inline-flex items-center gap-1 font-medium text-primary-700 hover:underline">Settings, Pay Rates<ArrowRight size={12} aria-hidden="true" /></Link>
          </p>
        </div>
      </section>
    );
  }

  if (!canView) {
    return (
      <section id="pay-rates" aria-label="Pay Rates" className="scroll-mt-24 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {header(null)}
        <div className="flex items-start gap-2.5 px-5 py-3.5 text-[13px] text-slate-600">
          <Info size={15} className="mt-0.5 shrink-0 text-slate-400" aria-hidden="true" />
          <p>Seeing this worker's rates needs the Pay Rates: View permission. Ask your company admin to add it to your role.</p>
        </div>
      </section>
    );
  }

  const current = data && data.current ? data.current : null;
  const defaults = data && data.default ? data.default : null;

  return (
    <section id="pay-rates" aria-label="Pay Rates" className="scroll-mt-24 overflow-hidden rounded-xl border border-slate-200 bg-white">
      {header(data && canEdit && (
        <button type="button" className="btn-secondary btn-sm" onClick={() => setEditOpen(true)}>
          <Pencil size={13} aria-hidden="true" />Edit rates
        </button>
      ))}

      {failed ? (
        <div className="flex flex-wrap items-center gap-3 px-5 py-4 text-[13px] text-slate-600">
          <span>We couldn't load this worker's rates.</span>
          <button type="button" className="btn-ghost btn-sm" onClick={load}>Try again</button>
        </div>
      ) : !data ? (
        <TableSkeleton rows={4} cols={4} />
      ) : (
        <>
          {!current && (
            <p className="border-b border-amber-100 bg-amber-50 px-5 py-2.5 text-[13px] text-amber-900">
              No rates saved for this worker yet. Shifts can't be paid until rates are set.{canEdit ? ' Use Edit rates to start from the defaults.' : ' Someone with the Pay Rates: Edit permission can set them.'}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[520px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="px-5 py-2.5 text-left">Rate</th>
                  <th scope="col" className="px-4 py-2.5 text-right">Default</th>
                  <th scope="col" className="px-4 py-2.5 text-right">This worker</th>
                  <th scope="col" className="px-5 py-2.5 text-left">Start date</th>
                  {xeroCol && <th scope="col" className="px-5 py-2.5 text-left">Xero</th>}
                </tr>
              </thead>
              <tbody>
                {RATES.map((r) => {
                  const mine = current ? current[r.key] : null;
                  const def = defaults ? defaults[r.key] : null;
                  const differs = current && defaults && Math.round(Number(mine) * 100) !== Math.round(Number(def) * 100);
                  return (
                    <tr key={r.key} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-2.5"><RateLabel rate={r} /></td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{rateText(def)}</td>
                      <td className={clsx('px-4 py-2.5 text-right font-semibold tabular-nums', differs ? 'text-primary-700' : 'text-slate-900')}>{rateText(mine)}</td>
                      <td className="px-5 py-2.5 tabular-nums text-slate-600">{current ? dmy(current.start_date) : 'Not set'}</td>
                      {xeroCol && <td className="px-5 py-2.5"><SyncPill s={xero.categories && xero.categories[r.key]} /></td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {xeroShown && <XeroEmployeeRow userId={userId} xero={xero} canLink={canLink && xero.connected} onChanged={loadXero} />}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 px-5 py-2.5">
            <p className="text-[12.5px] text-slate-500">
              {current && current.source && SOURCE[current.source] ? `${SOURCE[current.source]}. ` : ''}
              Rates in a different colour differ from the company defaults.
            </p>
            <button type="button" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary-700 hover:underline"
              onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
              <History size={13} aria-hidden="true" />{showHistory ? 'Hide rate history' : `Rate history (${history.length})`}
            </button>
          </div>
          {showHistory && (
            history.length === 0 ? (
              <p className="border-t border-slate-100 px-5 py-4 text-[13px] text-slate-500">No versions saved yet.</p>
            ) : (
              <div className="overflow-x-auto border-t border-slate-100 bg-slate-50/40">
                <table className="w-full min-w-[860px] text-[13px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11.5px] font-semibold text-slate-500">
                      <th scope="col" className="px-5 py-2 text-left">Start date</th>
                      {RATES.map((r) => (
                        <th key={r.key} scope="col" className="px-2.5 py-2 text-right whitespace-nowrap">
                          <span className="inline-flex items-center gap-1"><span className={clsx('h-2 w-2 rounded-full', r.dot)} aria-hidden="true" />{r.label}</span>
                        </th>
                      ))}
                      <th scope="col" className="px-5 py-2 text-left">Saved by</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((v, i) => (
                      <tr key={v.id || `${v.start_date}-${i}`} className="border-b border-slate-100 last:border-0">
                        <td className="px-5 py-2 whitespace-nowrap font-medium text-slate-800">
                          {dmy(v.start_date)}
                          {v.source && SOURCE[v.source] && <span className="block text-[11.5px] font-normal text-slate-500">{SOURCE[v.source]}</span>}
                        </td>
                        {RATES.map((r) => <td key={r.key} className="px-2.5 py-2 text-right tabular-nums text-slate-700">{rateText(v[r.key])}</td>)}
                        <td className="px-5 py-2 whitespace-nowrap text-slate-600">
                          {v.created_by_name || v.created_by_email || 'Rostery'}
                          {v.created_at && <span className="block text-[11.5px] text-slate-400">{dmy(v.created_at)}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      <EditDialog open={editOpen} userId={userId} name={name} current={current} defaults={defaults}
        onClose={() => setEditOpen(false)} onSaved={load} />
    </section>
  );
}
